import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Grid, MenuItem, IconButton, Chip, InputAdornment, Typography } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

// Medical problems values stored in English for database consistency
const medicalProblemsValues = [
  "High Blood Pressure",
  "Diabetes",
  "High Cholesterol",
  "Kidney Failure",
  "Heart Disease",
  "Hepatitis, Liver Problems",
  "Immune suppression",
  "Thyroid Disease",
  "Dry Eyes",
  "Other"
];

// Reusable chip input component
function ChipInput({ label, value, inputValue, onChange, helperText, error }) {
  const hasText = inputValue && inputValue.trim();
  
  const handleAdd = () => {
    if (hasText) {
      const newItems = [...(value || []), inputValue.trim()];
      onChange({ value: newItems, inputValue: '' });
    }
  };
  
  return (
    <>
      <TextField
        label={label}
        fullWidth
        value={inputValue || ''}
        onChange={(e) => onChange({ inputValue: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && hasText) {
            e.preventDefault();
            handleAdd();
          }
        }}
        onBlur={() => {
          // Auto-add when user clicks away if they typed something
          if (hasText) {
            handleAdd();
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleAdd}
                edge="end"
                disabled={!hasText}
                sx={{
                  color: hasText ? 'primary.main' : 'action.disabled',
                  backgroundColor: hasText ? 'primary.light' : 'transparent',
                  '&:hover': {
                    backgroundColor: hasText ? 'primary.main' : 'transparent',
                    color: hasText ? 'white' : 'action.disabled',
                  },
                  transition: 'all 0.3s ease',
                  transform: hasText ? 'scale(1.1)' : 'scale(1)',
                }}
              >
                <AddCircleOutlineIcon sx={{ fontSize: 32 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        error={error}
        helperText={
          error ? (typeof error === 'string' ? error : 'This field is required') : 
          hasText ? (
            <strong style={{ color: '#1976d2', fontSize: '0.9rem' }}>
              👉 CLICK THE + BUTTON or press ENTER to add this item! ⚠️ Add ONE item at a time (don't make a list)
            </strong>
          ) : 
          (helperText || "Add ONE item at a time, then click + or press Enter")
        }
      />
      
      {/* Display chips for each item */}
      {value && value.length > 0 && (
        <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block', fontWeight: 'bold' }}>
          ✓ {value.length} item{value.length !== 1 ? 's' : ''} added (click X on any chip to remove)
        </Typography>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
        {value?.map((item, index) => (
          <Chip
            key={index}
            label={item}
            onDelete={() => {
              const newItems = value.filter((_, i) => i !== index);
              onChange({ value: newItems });
            }}
            color="primary"
            variant="outlined"
            size="medium"
          />
        ))}
      </div>
    </>
  );
}

export default function Step3MedicalHistory({ formData, onChange, fieldErrors = {} }) {
  const { t } = useTranslation('medicalHistory');
  const { t: tCommon } = useTranslation('common');
  
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          select
          label={t('labels.medicalProblems')}
          fullWidth
          SelectProps={{
            multiple: true,
            renderValue: (selected) => (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {selected.map((value) => (
                  <div 
                    key={value}
                    style={{
                      backgroundColor: '#e0e0e0',
                      borderRadius: '16px',
                      padding: '4px 12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      margin: '2px',
                      fontSize: '0.875rem',
                      height: '32px'
                    }}
                  >
                    {value}
                    <IconButton
                      size="small"
                      onMouseDown={(e) => {
                        // Use onMouseDown instead of onClick to prevent the select from opening
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const currentProblems = formData.medical_problems || [];
                        const newValue = currentProblems.filter(item => item !== value);
                        onChange({ medical_problems: newValue });
                      }}
                      style={{
                        padding: '4px',
                        marginLeft: '4px',
                        color: 'rgba(0, 0, 0, 0.6)'
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
              </div>
            ),
            MenuProps: {
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            },
          }}
          value={formData.medical_problems || []}
          onChange={e => {
            // Prevent duplicate selections
            const uniqueValues = [...new Set(e.target.value)];
            onChange({ medical_problems: uniqueValues });
          }}
        >
          {medicalProblemsValues.map((opt) => (
            <MenuItem 
              key={opt} 
              value={opt}
              style={{
                backgroundColor: formData.medical_problems?.includes(opt) 
                  ? 'rgba(25, 118, 210, 0.08)' 
                  : 'inherit'
              }}
            >
              {opt}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {formData.medical_problems && formData.medical_problems.includes('Other') && (
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            {t('labels.otherMedicalProblems')} <strong>{t('helpers.addOneAtATime')}</strong>
          </Typography>
          <ChipInput
            label={t('labels.addOneMedicalProblem')}
            value={formData.other_medical_problems || []}
            inputValue={formData.other_medical_problem_input || ''}
            onChange={(update) => {
              const updates = {};
              if ('value' in update) updates.other_medical_problems = update.value;
              if ('inputValue' in update) updates.other_medical_problem_input = update.inputValue;
              onChange(updates);
            }}
            helperText={t('helpers.addThenClick')}
            error={!!fieldErrors.other_medical_problem}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          {t('labels.pastOperations')} <strong>{t('helpers.includeDate')}</strong>
        </Typography>
        <ChipInput
          label={t('labels.addOneOperation')}
          value={formData.past_operations_list || []}
          inputValue={formData.past_operations_input || ''}
          onChange={(update) => {
            const updates = {};
            if ('value' in update) updates.past_operations_list = update.value;
            if ('inputValue' in update) updates.past_operations_input = update.inputValue;
            onChange(updates);
          }}
          helperText={t('helpers.addOperationThenClick')}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          select
          required
          label={t('labels.takingMedication')}
          fullWidth
          value={formData.taking_medication || ''}
          onChange={e => onChange({ taking_medication: e.target.value })}
          error={!!fieldErrors.taking_medication}
          helperText={fieldErrors.taking_medication || ''}
        >
          <MenuItem value="Yes">{tCommon('buttons.yes')}</MenuItem>
          <MenuItem value="No">{tCommon('buttons.no')}</MenuItem>
        </TextField>
      </Grid>
      {formData.taking_medication === 'Yes' && (
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            {t('labels.medications')} <strong>{t('helpers.addOneAtATime')}</strong>
          </Typography>
          <ChipInput
            label={t('labels.addOneMedication')}
            value={formData.medications_list || []}
            inputValue={formData.medications_input || ''}
            onChange={(update) => {
              const updates = {};
              if ('value' in update) updates.medications_list = update.value;
              if ('inputValue' in update) updates.medications_input = update.inputValue;
              onChange(updates);
            }}
            helperText={t('helpers.addMedicationThenClick')}
            error={!!fieldErrors.medications}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField
          select
          required
          label={t('labels.hasAllergies')}
          fullWidth
          value={formData.has_allergies || ''}
          onChange={e => onChange({ has_allergies: e.target.value })}
          error={!!fieldErrors.has_allergies}
          helperText={fieldErrors.has_allergies || ''}
        >
          <MenuItem value="Yes">{tCommon('buttons.yes')}</MenuItem>
          <MenuItem value="No">{tCommon('buttons.no')}</MenuItem>
        </TextField>
      </Grid>
      {formData.has_allergies === 'Yes' && (
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            {t('labels.allergies')} <strong>{t('helpers.addOneAtATime')}</strong>
          </Typography>
          <ChipInput
            label={t('labels.addOneAllergy')}
            value={formData.allergies_list || []}
            inputValue={formData.allergies_input || ''}
            onChange={(update) => {
              const updates = {};
              if ('value' in update) updates.allergies_list = update.value;
              if ('inputValue' in update) updates.allergies_input = update.inputValue;
              onChange(updates);
            }}
            helperText={t('helpers.addAllergyThenClick')}
            error={!!fieldErrors.allergies}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <TextField
          label={t('labels.primaryCarePhysician')}
          fullWidth
          value={formData.primary_care_physician || ''}
          onChange={e => onChange({ primary_care_physician: e.target.value })}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          {t('labels.otherSpecialists')} <strong>{t('helpers.addOneAtATime')}</strong>
        </Typography>
        <ChipInput
          label={t('labels.addOneSpecialist')}
          value={formData.specialists_list || []}
          inputValue={formData.specialists_input || ''}
          onChange={(update) => {
            const updates = {};
            if ('value' in update) updates.specialists_list = update.value;
            if ('inputValue' in update) updates.specialists_input = update.inputValue;
            onChange(updates);
          }}
          helperText={t('helpers.addSpecialistThenClick')}
        />
      </Grid>
    </Grid>
  );
}
